/**
 * misradia — sort, filter and search for the compatible-products section (printer pages).
 *
 * The cards are rendered server-side (sections/compatible-products.liquid) so they stay indexable.
 * This script never moves or rebuilds them: sorting sets CSS `order` and filtering toggles a class,
 * which leaves the theme's Alpine components inside each card (quick add, variants) untouched.
 *
 * Search listens to an input outside the section — by default the Printer_search box
 * (`data-search-input`). Printer_search keeps its own model suggestions on the same keystrokes.
 * That box is also used to look up *other* printers, so a query that matches none of the products
 * on the page is ignored rather than emptying the grid.
 *
 * `data-cp-suggest` makes Printer_search stand down on this page. Its value picks what the box lists:
 * "none" (only the grid filters) or "products" (this page's matching products). Hebrew queries are
 * read via misradia-search-terms.js, so "ברדר" also matches titles that only say "BROTHER".
 */
(function () {
  var SEARCH_DEBOUNCE_MS = 120;
  var terms = window.misradiaSearchTerms;
  var readingsOf = terms ? terms.readings : function (q) { q = String(q || '').trim().toLowerCase(); return q ? [q] : []; };

  // Lower-case and drop spaces/punctuation, so "LC-422 XL" matches "lc422xl".
  function normalize(text) {
    return String(text || '').toLowerCase().replace(/[\s\-_./\\׳״'"`,()]+/g, '');
  }

  function init(root) {
    if (!root || root.dataset.cpReady) return;
    var grid = root.querySelector('.cp__grid');
    var controls = root.querySelector('.cp__controls');
    if (!grid || !controls) return;
    root.dataset.cpReady = 'true';

    var noResults = root.querySelector('.cp__no-results');
    var count = root.querySelector('.cp__count');
    var countText = root.dataset.countText || '';

    var items = Array.prototype.filter.call(grid.children, function (el) {
      return el.classList.contains('grid-item');
    }).map(function (el, index) {
      return {
        el: el,
        index: index,
        price: parseInt(el.dataset.price, 10) || 0,
        title: el.dataset.title || '',
        available: el.dataset.available === 'true',
        haystack: normalize(el.dataset.search),
        url: el.dataset.url,
        image: el.dataset.image,
        priceLabel: el.dataset.priceLabel,
      };
    });

    var sortSelect = controls.querySelector('.cp-sort');
    var inStock = controls.querySelector('.cp-instock');
    var minInput = controls.querySelector('.cp-min');
    var maxInput = controls.querySelector('.cp-max');
    var searchInputs = root.dataset.searchInput ? document.querySelectorAll(root.dataset.searchInput) : [];
    var query = '';

    var state = {sort: root.dataset.defaultSort || 'default'};
    if (sortSelect) {
      if (!sortSelect.querySelector('option[value="' + state.sort + '"]')) state.sort = 'default';
      sortSelect.value = state.sort;
    }

    function priceBound(input, fallback) {
      if (!input || input.value === '') return fallback;
      var value = parseFloat(input.value);
      return isNaN(value) ? fallback : value * 100;
    }

    function compare(a, b) {
      if (state.sort === 'price-asc') return a.price - b.price || a.index - b.index;
      if (state.sort === 'price-desc') return b.price - a.price || a.index - b.index;
      if (state.sort === 'title') return a.title.localeCompare(b.title, 'he') || a.index - b.index;
      return a.index - b.index;
    }

    function apply() {
      var min = priceBound(minInput, -Infinity);
      var max = priceBound(maxInput, Infinity);
      var onlyInStock = !!(inStock && inStock.checked);
      var matches = matcher(query);
      var searching = !!matches;
      if (searching && !items.some(matches)) searching = false;
      if (!searching) matches = function () { return true; };

      items.slice().sort(compare).forEach(function (item, rank) {
        item.el.style.order = rank;
      });

      var shown = 0;
      items.forEach(function (item) {
        var visible =
          (!onlyInStock || item.available) &&
          item.price >= min &&
          item.price <= max &&
          matches(item);
        item.el.classList.toggle('cp-is-hidden', !visible);
        if (visible) shown++;
      });

      if (noResults) noResults.hidden = shown > 0;
      if (count) {
        count.textContent = shown === items.length && !searching
          ? ''
          : countText.replace('[shown]', shown).replace('[total]', items.length);
      }
    }

    // null when there is nothing to search for; otherwise a test that passes if any reading of the
    // query (as typed, Hebrew brand → Latin, Hebrew layout → Latin) has all its words in the item.
    function matcher(text) {
      var queries = readingsOf(text).map(function (q) {
        return q.split(/\s+/).map(normalize).filter(Boolean);
      }).filter(function (tokens) { return tokens.length; });
      if (!queries.length) return null;
      return function (item) {
        return queries.some(function (tokens) {
          return tokens.every(function (token) { return item.haystack.indexOf(token) !== -1; });
        });
      };
    }

    var suggest = root.getAttribute('data-cp-suggest') === 'products';
    var maxSuggestions = parseInt(root.dataset.maxSuggestions, 10) || 8;

    function resultsFor(input) {
      var wrapper = input.closest('.printer-search__wrapper') || input.parentElement;
      return wrapper && wrapper.querySelector('.printer-search__results');
    }

    function hideSuggestions(results) {
      if (!results) return;
      results.innerHTML = '';
      results.style.display = 'none';
    }

    function renderSuggestions(input) {
      var results = resultsFor(input);
      if (!results) return;
      var test = matcher(input.value);
      var found = test ? items.filter(test).slice(0, maxSuggestions) : [];
      if (!found.length) return hideSuggestions(results);

      results.innerHTML = '';
      found.forEach(function (item) {
        var link = document.createElement('a');
        link.className = 'printer-search-item cp-suggest';
        link.href = item.url;
        if (item.image) {
          var img = document.createElement('img');
          img.className = 'cp-suggest__image';
          img.src = item.image;
          img.alt = '';
          img.loading = 'lazy';
          link.appendChild(img);
        }
        var text = document.createElement('span');
        text.className = 'cp-suggest__text';
        var title = document.createElement('span');
        title.className = 'cp-suggest__title';
        title.textContent = item.title;
        text.appendChild(title);
        if (item.priceLabel) {
          var price = document.createElement('span');
          price.className = 'cp-suggest__price';
          price.textContent = item.priceLabel;
          text.appendChild(price);
        }
        link.appendChild(text);
        results.appendChild(link);
      });
      results.style.display = 'block';
    }

    function moveActive(input, step) {
      var results = resultsFor(input);
      var links = results ? results.querySelectorAll('.cp-suggest') : [];
      if (!links.length) return false;
      var current = Array.prototype.findIndex.call(links, function (l) { return l.classList.contains('active'); });
      var next = current === -1 ? (step > 0 ? 0 : links.length - 1) : Math.min(Math.max(current + step, 0), links.length - 1);
      Array.prototype.forEach.call(links, function (l) { l.classList.remove('active'); });
      links[next].classList.add('active');
      links[next].scrollIntoView({block: 'nearest'});
      return true;
    }

    var timer;
    Array.prototype.forEach.call(searchInputs, function (input) {
      // A value can already be there (restored by the browser on back navigation).
      if (input.value) query = input.value;
      input.addEventListener('input', function () {
        query = input.value;
        clearTimeout(timer);
        timer = setTimeout(function () {
          apply();
          if (suggest) renderSuggestions(input);
        }, SEARCH_DEBOUNCE_MS);
      });
      if (suggest) {
        input.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            if (moveActive(input, e.key === 'ArrowDown' ? 1 : -1)) e.preventDefault();
          } else if (e.key === 'Enter') {
            var results = resultsFor(input);
            var active = results && results.querySelector('.cp-suggest.active');
            if (active) { e.preventDefault(); window.location.href = active.href; }
          } else if (e.key === 'Escape') {
            hideSuggestions(resultsFor(input));
          }
        });
      }
    });
    if (sortSelect) sortSelect.addEventListener('change', function () { state.sort = sortSelect.value; apply(); });
    if (inStock) inStock.addEventListener('change', apply);
    if (minInput) minInput.addEventListener('input', apply);
    if (maxInput) maxInput.addEventListener('input', apply);

    controls.hidden = false;
    apply();
  }

  function initAll() {
    document.querySelectorAll('.cp[data-cp-controls]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target && event.target.querySelector('.cp[data-cp-controls]'));
  });
})();
